import {
  Box,
  Dialog,
  Flex,
  HStack,
  IconButton,
  Portal,
  Spinner,
  Text,
  VisuallyHidden,
} from '@chakra-ui/react';
import type { Mention, MentionMediaType } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import { Xmark, NavArrowLeft, NavArrowRight } from 'iconoir-react';

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BackIconButton } from '@/components/ui/back-icon-button';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useBatchMatchScoresQuery } from '@/hooks/queries/use-batch-match-scores-query';
import { useMentionIntelligenceQuery } from '@/hooks/queries/use-mention-intelligence-query';
import { FeatureFlag } from '@fluenceur/types';
import { CreatorSummary, type MentionPreviewStats } from './CreatorSummary';
import { MentionInviteButton, MentionWishlistButton } from './MentionCreatorActions';
import { MentionMediaDisplay } from './MentionMediaDisplay';
import { DownloadMediaButton } from './DownloadMediaButton';
import { SaveToDriveIconButton } from './SaveToDriveIconButton';
import { ViewOnPlatformIconButton } from './ViewOnPlatformIconButton';
import { MentionTrackingSection } from './MentionTrackingSection';
import { MentionBoostPanel } from './MentionBoostPanel';
import { BoostInsightsMetaButton } from './boost-insights/BoostInsightsMetaButton';
import { isBoostDevPreviewActive } from '@/libs/boostDevPreview';

export interface PreviewMentionNavigator {
  currentIndex: number;
  total: number;
  hasPrev: boolean;
  hasNext: boolean;
  isLoadingNext: boolean;
  onPrev: () => void;
  onNext: () => void;
}

interface PreviewMentionModalProps {
  isOpen: boolean;
  onClose: () => void;
  mention: Mention;
  mediaUrl: string | null;
  mediaType: MentionMediaType;
  permalink?: string | null;
  hostedThumbnailUrl?: string | null;
  coverUrl?: string | null;
  nav?: PreviewMentionNavigator;
  onMediaError?: () => void;
}

const formatCounter = (current: number, total: number) => {
  if (total <= 99) {
    return `${String(current).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;
  }
  return `${current} / ${total}`;
};

export const PreviewMentionModal: React.FC<PreviewMentionModalProps> = ({
  isOpen,
  onClose,
  mention,
  mediaUrl,
  mediaType,
  permalink,
  hostedThumbnailUrl,
  coverUrl,
  nav,
  onMediaError,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isExpired = mention.isExpired;
  const { author } = mention;
  const [isBoostSetupActive, setIsBoostSetupActive] = React.useState(false);
  // `isBoostDevPreviewActive()` (DEV-only, behind ?boostDevPreview=1) force-mounts
  // the boost section without the feature flag so the picker can be smoke-tested.
  const partnershipAdsBoostEnabled =
    useFeatureFlag(FeatureFlag.PARTNERSHIP_ADS_BOOST) || isBoostDevPreviewActive();
  const { data: matchScores } = useBatchMatchScoresQuery(mention.brand.id, [], true, [author.id]);
  const creatorMatchScore = matchScores?.[author.id] ?? null;
  const {
    data: intelligence,
    isLoading: isIntelligenceLoading,
    isError: isIntelligenceError,
  } = useMentionIntelligenceQuery({
    mentionId: mention.id,
  });
  // The newest timeline point already carries the four preview counters.
  const mentionStats: MentionPreviewStats | undefined = intelligence?.timeline.points.at(-1);

  React.useEffect(() => {
    setIsBoostSetupActive(false);
  }, [isOpen, mention.id]);

  const nextDisabled = nav ? isBoostSetupActive || (!nav.hasNext && !nav.isLoadingNext) : true;
  const prevDisabled = nav ? isBoostSetupActive || !nav.hasPrev : true;
  const renderDecisionScroll = (
    boostPanel?: React.ReactNode,
    hasStickyFooter = false,
    isBoosted = false,
    boostLaunchedAt: string | null = null
  ) => (
    <Box
      flex="1"
      overflowY="auto"
      px={{ base: 4, md: 6 }}
      pb={hasStickyFooter ? { base: 28, md: 32 } : 6}
    >
      <CreatorSummary
        mention={mention}
        stats={mentionStats}
        isStatsLoading={isIntelligenceLoading}
      />

      <MentionInviteButton mention={mention} />

      <MentionTrackingSection
        intelligence={intelligence}
        creatorMatchScore={creatorMatchScore}
        isLoading={isIntelligenceLoading}
        isError={isIntelligenceError}
        boostPanel={boostPanel}
        platform={mention.platform}
        isBoosted={isBoosted}
        boostLaunchedAt={boostLaunchedAt}
        onReconnect={() => navigate('/settings/integrations')}
      />
    </Box>
  );
  const renderDefaultRightHeader = (isBoosted = false) => (
    <HStack
      justify="space-between"
      px={4}
      py={3}
      gap={1}
      borderBottom="1px solid"
      borderColor="gray.100"
      bg="white"
    >
      <HStack gap={1}>
        <SaveToDriveIconButton
          mentionId={mention.id}
          platform={mention.platform}
          isExpired={isExpired}
          isCreatorEnrolled={author.isEnrolled}
          variant="ghost"
          colorPalette="gray"
        />
        <DownloadMediaButton
          mentionId={mention.id}
          platform={mention.platform}
          isExpired={isExpired}
          variant="ghost"
          colorPalette="gray"
          size="sm"
          showText={false}
          minW={8}
          boxSize={8}
          p={0}
        />
      </HStack>
      {/* The wishlist star sits just before the launched-boost Meta deep-link;
          the Meta button renders nothing until a live ad reference exists. */}
      <HStack gap={1}>
        <MentionWishlistButton mention={mention} />
        {isBoosted && <BoostInsightsMetaButton mentionId={mention.id} />}
      </HStack>
    </HStack>
  );
  const renderBoostPaneHeader = (onBack: () => void, titleKey: string) => (
    <HStack
      justify="space-between"
      px={4}
      py={3}
      gap={2}
      borderBottom="1px solid"
      borderColor="gray.100"
      bg="white"
    >
      <HStack gap={2} minW={0}>
        <BackIconButton onClick={onBack} />
        <Text fontSize="sm" fontWeight="semibold" color="gray.900">
          {t(titleKey)}
        </Text>
      </HStack>
    </HStack>
  );

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={({ open }) => !open && onClose()}
      placement="center"
      size="xl"
    >
      <Portal>
        <Dialog.Backdrop bg="blackAlpha.600" backdropFilter="blur(8px)" />
        <Dialog.Positioner>
          <Dialog.Content rounded="2xl" overflow="hidden" maxW="920px" maxH="85vh" mx={4}>
            <VisuallyHidden>
              <Dialog.Title>
                {t('mentions.preview.title', { username: author.username })}
              </Dialog.Title>
            </VisuallyHidden>
            <Dialog.Body p={0}>
              <Flex direction={{ base: 'column', md: 'row' }} h={{ md: '75vh' }} maxH="85vh">
                <Box
                  flex={{ md: '0 0 480px' }}
                  h={{ base: '50vh', md: '100%' }}
                  bg="black"
                  position="relative"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  overflow="hidden"
                  px={6}
                  py={12}
                >
                  <HStack
                    position="absolute"
                    top={3}
                    left={3}
                    right={3}
                    zIndex={1}
                    gap={2}
                    align="center"
                  >
                    <IconButton
                      aria-label={t('mentions.preview.close')}
                      onClick={onClose}
                      minW={6}
                      w={6}
                      h={6}
                      bg="whiteAlpha.900"
                      color="black"
                      rounded="full"
                      variant="ghost"
                      backdropFilter="blur(8px)"
                      _hover={{ bg: 'white' }}
                    >
                      <Xmark width={14} height={14} strokeWidth={2} />
                    </IconButton>
                    {/* On the media, beside the close button: the caption overlay
                        covers the bottom of the frame, never this bar. */}
                    <ViewOnPlatformIconButton
                      withLabel
                      platform={mention.platform}
                      permalink={permalink}
                      profileUrl={author.platformUrl}
                      data-testid="mention-preview-view-on-platform"
                    />
                    {nav && (
                      <HStack gap={1} ml="auto" align="center">
                        <Box
                          role="status"
                          aria-live="polite"
                          aria-label={t('mentions.preview.counter', {
                            current: nav.currentIndex + 1,
                            total: nav.total,
                          })}
                          bg="whiteAlpha.900"
                          color="black"
                          rounded="full"
                          px={2.5}
                          py={1}
                          fontSize="xs"
                          fontWeight="medium"
                          fontVariantNumeric="tabular-nums"
                          backdropFilter="blur(8px)"
                          data-testid="mention-preview-counter"
                        >
                          {formatCounter(nav.currentIndex + 1, nav.total)}
                        </Box>
                        <IconButton
                          aria-label={t('mentions.preview.previous')}
                          data-testid="mention-preview-prev"
                          minW={6}
                          w={6}
                          h={6}
                          variant="ghost"
                          rounded="full"
                          color="black"
                          bg="whiteAlpha.900"
                          backdropFilter="blur(8px)"
                          _hover={{ bg: 'white' }}
                          disabled={prevDisabled}
                          onClick={nav.onPrev}
                        >
                          <NavArrowLeft width={14} height={14} />
                        </IconButton>
                        <IconButton
                          aria-label={t('mentions.preview.next')}
                          data-testid="mention-preview-next"
                          minW={6}
                          w={6}
                          h={6}
                          variant="ghost"
                          rounded="full"
                          color="black"
                          bg="whiteAlpha.900"
                          backdropFilter="blur(8px)"
                          _hover={{ bg: 'white' }}
                          disabled={nextDisabled || nav.isLoadingNext}
                          onClick={nav.onNext}
                        >
                          {nav.isLoadingNext ? (
                            <Spinner size="xs" color="black" />
                          ) : (
                            <NavArrowRight width={14} height={14} />
                          )}
                        </IconButton>
                      </HStack>
                    )}
                  </HStack>

                  <MentionMediaDisplay
                    mediaUrl={mediaUrl}
                    mediaType={mediaType}
                    isExpired={isExpired}
                    mentionType={mention.mentionType}
                    platform={mention.platform}
                    permalink={permalink}
                    hostedThumbnailUrl={hostedThumbnailUrl}
                    coverUrl={coverUrl}
                    onMediaError={onMediaError}
                  />
                  {mention.caption && (
                    <Box
                      position="absolute"
                      left={3}
                      right={3}
                      bottom={2}
                      zIndex={1}
                      maxH="28%"
                      overflowY="auto"
                      bg="blackAlpha.600"
                      color="white"
                      rounded="md"
                      px={3}
                      py={2.5}
                      backdropFilter="blur(10px)"
                      opacity={0.88}
                      transform="translateY(max(0px, calc(100% - 74px)))"
                      transition="transform 180ms ease, max-height 180ms ease, opacity 180ms ease, background 180ms ease"
                      tabIndex={0}
                      _hover={{
                        maxH: '72%',
                        bg: 'blackAlpha.800',
                        opacity: 1,
                        transform: 'translateY(0)',
                      }}
                      _focusWithin={{
                        maxH: '72%',
                        bg: 'blackAlpha.800',
                        opacity: 1,
                        transform: 'translateY(0)',
                      }}
                      _focusVisible={{
                        outline: '2px solid',
                        outlineColor: 'whiteAlpha.700',
                        outlineOffset: '2px',
                      }}
                      data-testid="mention-caption-overlay"
                    >
                      <Text fontSize="sm" lineHeight="tall">
                        {mention.caption}
                      </Text>
                    </Box>
                  )}
                </Box>

                <Flex flex="1" direction="column" bg="white" overflow="hidden" position="relative">
                  {partnershipAdsBoostEnabled ? (
                    <MentionBoostPanel
                      mentionId={mention.id}
                      identity={{
                        brandName: mention.brand.name,
                        username: author.username,
                        creatorAvatarUrl: author.profilePictureUrl,
                      }}
                      onSetupOpenChange={setIsBoostSetupActive}
                    >
                      {({
                        panel,
                        footer,
                        isRequestOpen,
                        isSetupOpen,
                        isBoosted,
                        boostLaunchedAt,
                        onCloseRequest,
                        onCloseSetup,
                        onCloseProposal,
                        requestPanel,
                        setupPanel,
                        proposalPanel,
                      }) =>
                        isRequestOpen ? (
                          <>
                            {renderBoostPaneHeader(
                              onCloseRequest,
                              'mentions.boost.confirm.requestTitle'
                            )}
                            {requestPanel}
                          </>
                        ) : proposalPanel ? (
                          <>
                            {renderBoostPaneHeader(
                              onCloseProposal,
                              'mentions.boost.proposal.title'
                            )}
                            {proposalPanel}
                          </>
                        ) : isSetupOpen ? (
                          <>
                            {renderBoostPaneHeader(onCloseSetup, 'mentions.boost.setup.title')}
                            {setupPanel}
                          </>
                        ) : (
                          <>
                            {renderDefaultRightHeader(isBoosted)}
                            {renderDecisionScroll(panel, true, isBoosted, boostLaunchedAt)}
                            {footer}
                          </>
                        )
                      }
                    </MentionBoostPanel>
                  ) : (
                    <>
                      {renderDefaultRightHeader()}
                      {renderDecisionScroll()}
                    </>
                  )}
                </Flex>
              </Flex>
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default PreviewMentionModal;
